import { useState } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddProject() {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'in_progress',
    instructions: '',
    progress_percentage: 0
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        progress_percentage: parseInt(formData.progress_percentage) || 0
      };

      await axios.post(`${API}/projects`, dataToSend, {
        withCredentials: true
      });

      toast.success('Project created successfully!');
      navigate('/projects');
    } catch (error) {
      logger.error('Failed to create project:', error);
      toast.error('Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/projects')}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
            Start New Project
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            Track your knitting or crochet project
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                Project Name *
              </Label>
              <Input
                data-testid="input-name"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Baby Blanket, Winter Scarf"
                required
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-medium mb-2 block">
                Description
              </Label>
              <Textarea
                data-testid="input-description"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="What are you making?"
                rows={3}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="instructions" className="text-sm font-medium mb-2 block">
                Instructions / Notes
              </Label>
              <Textarea
                data-testid="input-instructions"
                id="instructions"
                name="instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                placeholder="Pattern notes, stitch instructions, or any important details..."
                rows={6}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="progress_percentage" className="text-sm font-medium mb-2 block">
                Initial Progress (%)
              </Label>
              <Input
                data-testid="input-progress"
                id="progress_percentage"
                name="progress_percentage"
                type="number"
                min="0"
                max="100"
                value={formData.progress_percentage}
                onChange={handleInputChange}
                className="rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm"
              />
            </div>

            <div>
              <Label htmlFor="status" className="text-sm font-medium mb-2 block">
                Status
              </Label>
              <select
                data-testid="input-status"
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full rounded-xl border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-200 shadow-sm px-3 py-2"
              >
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-4">
            <Button
              data-testid="cancel-button"
              type="button"
              variant="ghost"
              onClick={() => navigate('/projects')}
              className="rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
            >
              Cancel
            </Button>
            <Button
              data-testid="save-project-button"
              type="submit"
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Project
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
